import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Scissors,
  Recycle,
  Tag,
  Layers,
  Boxes,
  Sparkles,
  Calendar,
  Lock,
  CreditCard,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { getCityPageBySlug } from "@/data/citiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Expédition directe Boulogne ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison locale
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Expédition de vos fournitures directement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Simplifiez l&apos;organisation de votre projet et évitez la corvée
            du transport d&apos;emballages volumineux dans le centre-ville ou
            vers les bords de Seine. Nous assurons l&apos;expédition et la
            livraison de toutes vos fournitures directement à votre adresse à
            {" "}{city.name} et dans toute la région. Grâce à notre propre
            flotte de véhicules, sans aucun intermédiaire, nous vous
            garantissons un service de proximité rapide, fiable et ponctuel.
            Recevez votre matériel au pied de votre porte pour commencer à
            emballer vos affaires sans perdre de temps.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Propre flotte de transport",
              body: "Aucun sous-traitant, nous livrons vos cartons nous-mêmes avec nos propres véhicules pour un soin maximal.",
              Icon: Truck,
            },
            {
              title: "Rapidité et ponctualité",
              body: "Des créneaux respectés pour vous permettre de commencer vos préparatifs sans perdre une minute.",
              Icon: CircleCheck,
            },
            {
              title: "Soin de vos produits",
              body: "Vos cartons et protections arrivent en parfait état, prêts à l'emploi, directement entre vos mains.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Accessoires de fermeture et scellage ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Fermeture &amp; scellage
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            L&apos;importance des accessoires de fermeture et de scellage
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un bon carton ne suffit pas s&apos;il n&apos;est pas correctement
            fermé. Pour garantir la sécurité de vos biens, notre boutique
            propose une sélection complète d&apos;accessoires de fermeture et
            de scellage professionnels, incluant des adhésifs haute résistance
            et des dévidoirs ergonomiques.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Le choix d&apos;un scellage de qualité est une étape cruciale pour
            plusieurs raisons :
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              n: "1",
              title: "Solidité structurelle",
              body: "Un adhésif professionnel renforce le fond et les rabats de vos cartons, évitant ainsi qu'ils ne cèdent sous le poids de vos objets lourds pendant le transport.",
            },
            {
              n: "2",
              title: "Protection contre les éléments",
              body: "Un scellage hermétique met vos affaires à l'abri de la poussière, de l'humidité et des salissures durant la manipulation et le stockage.",
            },
            {
              n: "3",
              title: "Gain de temps",
              body: "Utiliser les bons outils de fermeture vous permet de sécuriser chaque boîte en quelques secondes, rendant vos préparatifs bien plus efficaces.",
            },
          ].map(({ n, title, body }) => (
            <div
              key={n}
              className="relative bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5"
            >
              <div className="absolute top-4 right-4 text-[34px] font-extrabold text-[#237cb5]/15 leading-none">
                {n}
              </div>
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#237cb5] text-white text-[14px] font-extrabold">
                  {n}
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-cta-fermeture"
          >
            <Scissors className="h-4 w-4" strokeWidth={2.6} />
            Voir nos accessoires de fermeture
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 4 — Meilleures ventes Boulogne ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#FF7A00" }}>
            Top ventes locales
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos meilleures ventes à {city.name} : les indispensables de votre
            déménagement
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Découvrez les fournitures les plus plébiscitées par les habitants
            de {city.name} pour réussir leurs emballages. De notre carton
            standard ultra-polyvalent aux kits complets adaptés aux
            appartements et maisons de la région, ces produits phares
            combinent robustesse professionnelle et écoresponsabilité. Faites
            confiance aux choix de nos clients locaux pour sécuriser vos
            objets précieux au meilleur prix.
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to="/cartons-demenagement"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-cta-bestsellers"
            >
              Voir tous les cartons
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/packs-cartons"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="city-cta-kits"
            >
              Choisir un kit
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Autres villes dans le 92 ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Cartons déménagement {city.deptName}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Cartons et fournitures de déménagement dans les autres villes du
            {" "}{city.deptName} ({city.deptCode})
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            cartonmove livre également les villes voisines du {city.deptCode}.
            Voici les communes des Hauts-de-Seine où nos équipes interviennent
            le plus souvent&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement/${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
